import { Vector2, Vector3 } from 'three';

import { slopePercentToHeightDiff } from '../../../utils/math/slope';
import { isDefined } from '../../../utils/variables';

export type LineReference = {
  /** Direction in the horizontal plane that the angle of a new line is relative to. */
  direction: Vector2;
  /** The line that the direction is taken from, undefined if there is none and the direction is east. */
  line?: { start: Vector3; end: Vector3 };
};

export type LineConstraints = {
  /** Direction in the horizontal plane that the angle is relative to, e.g. the previous line. */
  referenceDirection: Vector2;
  /** Snaps the direction of the line to the closest of these angles (radians) from the reference direction. */
  angles?: number[];
  /** Length of the line, either horizontally (2d) or along the line (3d). */
  length?: { value: number; mode: '2d' | '3d' };
  /** Slope in percent that sets the height of the end point relative to the start point. */
  slopePercent?: number;
};

/**
 * Angles (radians) from the reference direction that are along or perpendicular to the reference line.
 */
export const perpendicularAngles = [0, Math.PI / 2, Math.PI, -Math.PI / 2];

/**
 * Gets the angles (radians) from the reference direction where a line has the given angle to
 * the reference line, to either side of it and going either forwards or backwards.
 */
export const getAnglesToLine = (angle: number) => [angle, -angle, Math.PI - angle, angle - Math.PI];

/**
 * Snaps a direction in the horizontal plane to the closest of the angles (radians)
 * from the reference direction, keeping its length.
 */
export const snapDirectionToAngle = (
  direction: Vector2,
  referenceDirection: Vector2,
  angles: number[],
): Vector2 => {
  const relativeAngle = direction.angle() - referenceDirection.angle();

  // Compare the angles between -PI and PI, so that e.g. 350 degrees is close to -5 degrees.
  const distanceTo = (angle: number) => {
    const difference = relativeAngle - angle;
    return Math.abs(Math.atan2(Math.sin(difference), Math.cos(difference)));
  };

  const closest = angles.reduce((a, b) => (distanceTo(b) < distanceTo(a) ? b : a));
  const angle = referenceDirection.angle() + closest;

  return new Vector2(Math.cos(angle), Math.sin(angle)).multiplyScalar(direction.length());
};

/**
 * Applies the constraints to a line going from origin to point.
 *
 * The angle and length are applied in the horizontal plane, then the slope sets the height
 * of the point relative to the origin. Without a slope the height of the point is kept.
 *
 * Returns undefined if the length along the line is shorter than the height difference to the point.
 */
export const applyLineConstraints = (
  origin: Vector3,
  point: Vector3,
  constraints: LineConstraints,
): Vector3 | undefined => {
  let horizontal = new Vector2(point.x - origin.x, point.y - origin.y);

  if (isDefined(constraints.angles)) {
    horizontal = snapDirectionToAngle(
      horizontal,
      constraints.referenceDirection,
      constraints.angles,
    );
  }

  if (constraints.length?.mode === '2d') {
    horizontal.setLength(constraints.length.value);
  } else if (constraints.length?.mode === '3d') {
    const length = constraints.length.value;

    if (isDefined(constraints.slopePercent)) {
      // The height grows with the horizontal length, so the horizontal length is shortened by the slope.
      const slope = constraints.slopePercent * 0.01;
      horizontal.setLength(length / Math.sqrt(1 + slope * slope));
    } else {
      const heightDifference = point.z - origin.z;

      if (Math.abs(heightDifference) > length) {
        return undefined;
      }

      horizontal.setLength(Math.sqrt(length * length - heightDifference * heightDifference));
    }
  }

  const z = isDefined(constraints.slopePercent)
    ? origin.z + slopePercentToHeightDiff(constraints.slopePercent, horizontal.length())
    : point.z;

  return new Vector3(origin.x + horizontal.x, origin.y + horizontal.y, z);
};
